import { useCallback, useEffect, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import { NovedadesExportService } from "@services/NovedadesExportService";

export type ExportNovedadesConfigurationTabID = "statusMapping" | "extraHours" | "colppy";

interface UseExportNovedadesConfigurationModalViewModelProps {
    isOpen: boolean;
    orgID: string;
    exportService: NovedadesExportService;
}

export const useExportNovedadesConfigurationModalViewModel = ({
    isOpen,
    orgID,
    exportService,
}: UseExportNovedadesConfigurationModalViewModelProps) => {
    const [selectedTab, setSelectedTab] = useState<ExportNovedadesConfigurationTabID>("statusMapping");
    const [colppyCode, setColppyCode] = useState("");
    const [savedColppyCode, setSavedColppyCode] = useState("");
    const [colppyLoading, setColppyLoading] = useState(true);
    const [colppySaving, setColppySaving] = useState(false);
    const [colppyError, setColppyError] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (isOpen) {
            setSelectedTab("statusMapping");
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (!orgID) {
            setColppyCode("");
            setSavedColppyCode("");
            setColppyLoading(false);
            return;
        }

        let cancelled = false;

        const loadColppyConfiguration = async () => {
            setColppyLoading(true);
            setColppyError(undefined);

            try {
                const configuration = await exportService.getColppyConfiguration(orgID);
                if (cancelled) {
                    return;
                }
                const code = configuration.organizationColppyCode ?? "";
                setColppyCode(code);
                setSavedColppyCode(code);
            } catch (loadError) {
                console.error(loadError);
                if (!cancelled) {
                    setColppyError("No se pudo cargar la configuración de Colppy.".localized());
                }
            } finally {
                if (!cancelled) {
                    setColppyLoading(false);
                }
            }
        };

        void loadColppyConfiguration();

        return () => {
            cancelled = true;
        };
    }, [exportService, isOpen, orgID]);

    const hasColppyChanges = colppyCode.trim() !== savedColppyCode;

    const cancelColppy = useCallback(() => {
        setColppyCode(savedColppyCode);
        setColppyError(undefined);
    }, [savedColppyCode]);

    const saveColppyConfiguration = useCallback(async (): Promise<boolean> => {
        const nextCode = colppyCode.trim();
        if (nextCode === savedColppyCode) {
            return true;
        }

        setColppySaving(true);
        setColppyError(undefined);
        try {
            const configuration = await exportService.updateColppyConfiguration(orgID, nextCode);
            const updatedCode = configuration.organizationColppyCode ?? "";
            setColppyCode(updatedCode);
            setSavedColppyCode(updatedCode);
            NovedadesExportAnalyticsHelper.trackColppyConfigurationSaved({
                hasCode: updatedCode.length > 0,
            });
            return true;
        } catch (saveError) {
            console.error(saveError);
            setColppyError("No se pudo guardar la configuración de Colppy.".localized());
            return false;
        } finally {
            setColppySaving(false);
        }
    }, [colppyCode, exportService, orgID, savedColppyCode]);

    return {
        selectedTab,
        setSelectedTab: (tab: string) => setSelectedTab(tab as ExportNovedadesConfigurationTabID),
        colppyCode,
        setColppyCode,
        colppyLoading,
        colppySaving,
        colppyError,
        hasColppyChanges,
        cancelColppy,
        saveColppyConfiguration,
    };
};
